// Admin CRUD for the vendor master (migration 009, ERP expansion chunk 1).
// One row per company Firefly buys from — every purchase order and receipt
// box will eventually point back here. BOTH instances (universal to the ERP
// expansion, see vendors-service.js).
//
// GET /api/procurement/vendors returns the LEAN list projection (open to any
// authed user, always has been). Editing needs the full row — contacts,
// address, payment terms, QBO ref — which only comes back from the gated
// GET /api/procurement/vendors/:id, so Edit fetches the detail row before
// opening the form rather than editing off the list row directly.
//
// No delete anywhere: vendors are referenced by purchase orders (ON DELETE
// RESTRICT). Retiring one is is_disabled=true, which keeps history readable.

import React, { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import PaymentClockFields from '../Procurement/PaymentClockFields';
import { useToast } from '../components/Toast/ToastContext';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';
import DrawerShell from '../Receiving/DrawerShell';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import { FilterBar, FilterSearch } from '../components/FilterBar';
import useUserStore from '../stores/useUserStore';
import VendorPreviewModal from '../Procurement/VendorPreviewModal';
import { formatType, VendorTypeOption } from '../Procurement/vendorTypeLabels';
import { DataTable, type GroupByOpts } from '../components/table/DataTable';
import { modalLabel } from '../components/review/fieldStyles';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Checkbox } from '../components/ui/Checkbox';
import { Field, TextArea } from '../components/ui/Field';
import Select from '../components/ui/Select';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import Skeleton, { SkeletonGroup } from '../components/ui/Skeleton';

// Lean list projection — matches vendors-service.js LIST_COLUMNS (vendor_type
// renamed to `type` on the way out).
// Exported: the PO import review screen's vendor preview modal renders this
// same detail row rather than redefining the field list a second time.
export interface VendorListRow {
  id: string;
  code: string | null;
  name: string;
  type: string;
  notes: string | null;
  on_hold: boolean;
  // hold_reason is not in the list projection (free text, commercially
  // sensitive) — it comes back on the gated detail row when editing.
  release_date: string | null;
  is_disabled: boolean;
  /** 029: this vendor holds our material, and has a paired row in Locations. */
  can_receive: boolean;
  /** 034/035: the computable half of payment_terms. NULL where the prose
   *  cannot be reduced to a number, e.g. a split deposit schedule. */
  payment_terms_days: number | null;
  payment_terms_basis: string | null;
}

// Full row — everything the list omits, only available via GET /vendors/:id.
export interface VendorFullRow extends VendorListRow {
  hold_reason: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  payment_terms: string | null;
  qbo_vendor_ref: string | null;
  created_at: string;
  updated_at: string;
}

// The vendor_type taxonomy's labels live in vendorTypeLabels.ts (a leaf module
// with no imports of its own) so this file and VendorPreviewModal.tsx can both
// depend on it without depending on EACH OTHER — VendorsAdmin now opens that
// modal for the row-click preview below, and the modal needs these labels too.
// Re-exported here so every existing caller (PoImportVendorTriage.tsx,
// PoImportDocumentCard.tsx) keeps importing from this file unchanged.
export { formatType };

// The vendor form's field and label ARE the new-item modal's. VendorForm is
// mounted twice — this admin page and the New Item wizard's in-place vendor
// step — so a field that only matched one of its two homes would look correct
// on the page and wrong inside the wizard.
const labelClass = modalLabel;

// ---------------------------------------------------------------------------
// The vendor list: the shared DataTable's columns.
//
// Module scope, per the DataTable contract — the array never changes identity
// and no cell closes over page state. Edit's handler AND its per-row busy id
// arrive on the table's `meta` and are read at render time, so a fetch in
// flight re-renders the cells without remounting them. Never wrap one of these
// in React.memo: it would skip the parent's render and read a stale meta.
//
// No `sortable` and no accessorFn. The API returns the rows in canonical
// type-then-name order and this page has never offered a column sort; the
// search box above the table is the only thing that changes what is in it.
//
// The row is the preview's click target (role=button, its own tab stop) — that
// lives in `rowProps` on the page, because it is a property of the row and not
// of any column.
// ---------------------------------------------------------------------------

interface VendorTableMeta {
  /** The row whose detail fetch is in flight, or null. */
  loadingEditId: string | null;
  onEdit: (row: VendorListRow) => void;
  /**
   * key -> the label an admin chose for it. On `meta` rather than imported,
   * because the labels now come from the API and a module-scope cell cannot
   * close over page state. Read at render time, never memoized into a cell.
   */
  labelForType: (key: string) => string;
}

const VENDOR_COLUMNS: ColumnDef<VendorListRow, any>[] = [
  {
    id: 'code',
    header: 'Code',
    meta: { cellClass: 'font-mono text-xs text-primary' },
    cell: ({ row }) => row.original.code || '—',
  },
  {
    id: 'name',
    header: 'Name',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => (
      <>
        {row.original.name}
        {row.original.can_receive && (
          <Tooltip label="Holds our inventory. Appears in the receive screen as a location.">
            <Tag variant="code" size="sm" className="ml-2">holds stock</Tag>
          </Tooltip>
        )}
      </>
    ),
  },
  {
    id: 'type',
    header: 'Type',
    meta: { cellClass: 'text-on-surface-variant text-xs' },
    cell: ({ row, table }) =>
      (table.options.meta as VendorTableMeta).labelForType(row.original.type),
  },
  {
    id: 'hold',
    header: 'Hold',
    meta: { cellClass: 'text-xs' },
    cell: ({ row }) =>
      row.original.on_hold ? (
        <Tag tone="warn">
          On hold{row.original.release_date ? ` · lifts ${row.original.release_date}` : ''}
        </Tag>
      ) : (
        <span className="text-on-surface-variant">—</span>
      ),
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center' },
    cell: ({ row }) =>
      row.original.is_disabled ? (
        <span className="text-on-surface-variant text-xs">Disabled</span>
      ) : (
        <span className="text-green-300">●</span>
      ),
  },
  {
    id: 'edit',
    header: 'Edit',
    meta: { align: 'right' },
    cell: (info) => {
      const { loadingEditId, onEdit } = info.table.options.meta as VendorTableMeta;
      const r = info.row.original;
      const busy = loadingEditId === r.id;
      return (
        <button
          type="button"
          onClick={(e) => {
            // The row itself is the preview's click target — without this, Edit
            // would open the edit drawer AND the row's onClick would bubble up
            // and open the preview on top of it.
            e.stopPropagation();
            onEdit(r);
          }}
          disabled={busy}
          className={buttonClass('quiet', 'sm')}
        >
          {busy ? 'Loading…' : 'Edit'}
        </button>
      );
    },
  },
];

const VendorsAdmin: React.FC = () => {
  const api = useApi();
  const user = useUserStore((state) => state.user);
  const toast = useToast();

  const [rows, setRows] = useState<VendorListRow[]>([]);
  const [types, setTypes] = useState<VendorTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<VendorFullRow | null>(null);
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);
  const [previewVendorId, setPreviewVendorId] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const data = (await api.get('/procurement/vendors?includeDisabled=1')) as {
        vendors: VendorListRow[];
        types: VendorTypeOption[];
      };
      setRows(data.vendors || []);
      setTypes(data.types || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || (r.code || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  /**
   * An admin renames a type and every vendor wearing it re-labels at once,
   * because vendors are attached by KEY and the label is looked up. A key the
   * payload no longer carries (a retired type still on an old vendor) falls back
   * to a title-cased render of the key itself, so a row never goes blank.
   */
  const labelForType = useMemo(() => {
    const byKey = new Map(types.map((t) => [t.key, t.label]));
    return (key: string) => byKey.get(key) || formatType(key);
  }, [types]);

  // Rows already arrive sorted canonical-type-then-name from the API. Group
  // them under a type header when not searching; a search can span multiple
  // types, so it renders flat instead (the gate is at the prop below).
  //
  // ORDER IS THE ROWS' OWN SEQUENCE, not alphabetical. The API sorts by
  // array_position over its VENDOR_TYPES list (vendors-service.js), so PCBA
  // leads and Prop trails — a meaning the taxonomy carries and the alphabet
  // does not. The shared table's default would localeCompare the keys and
  // reshuffle them, so first appearance is ranked here and the order callback
  // sorts through that rank. It asserts nothing of its own about the taxonomy:
  // wherever the query put a type, including an off-taxonomy one (the column
  // has no CHECK, and array_position's NULL sorts those last), the bands follow.
  // Derived in the SAME memo that builds the groupBy object, so the rank and
  // the keys can never come from different row sets — a type absent from a
  // filtered set simply has no key to place.
  const vendorGroupBy = useMemo<GroupByOpts<VendorListRow>>(() => {
    const rank = new Map<string, number>();
    for (const r of filtered) if (!rank.has(r.type)) rank.set(r.type, rank.size);
    const last = rank.size;
    return {
      key: (r) => r.type,
      order: (keys) =>
        [...keys].sort((a, b) => (rank.get(a) ?? last) - (rank.get(b) ?? last)),
      renderHeader: (type, list) => `${labelForType(type)} · ${list.length}`,
    };
  }, [filtered, labelForType]);

  const handleEdit = async (row: VendorListRow) => {
    setLoadingEditId(row.id);
    try {
      const full = (await api.get(`/procurement/vendors/${row.id}`)) as VendorFullRow;
      setEditing(full);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load vendor');
    } finally {
      setLoadingEditId(null);
    }
  };

  return (
    <PageLayout kind="workspace" className="pt-4 space-y-5">
      <PageHeader
        title="Vendors"
        helpTitle="About vendors"
        lede="Every company Firefly buys parts or services from, one row each."
        help={
          <>
            <p>
              A vendor is any company Firefly buys parts or services from, like a board
              manufacturer or a case supplier. This page is the master list, one row per
              vendor, with contact details and buying rules. Every purchase order points
              back to one of these rows, so keeping them accurate keeps the whole buying
              history readable.
            </p>
            <p className="font-medium text-on-surface">Putting a vendor on hold</p>
            <p>
              Hold marks a vendor Firefly should not buy from right now. Submitting a
              purchase order to a held vendor is blocked until the hold lifts, and the
              hold is visible to everyone who orders. It does not touch anything
              already ordered or received, that
              history stays exactly as it is. You have to give a reason when you put a
              vendor on hold, so whoever looks at it later (maybe not you) knows why it
              is frozen and can decide when to lift it. The release date is just a
              reminder of when the hold is expected to lift, it does not lift on its own.
            </p>
            <p className="font-medium text-on-surface">Vendors that hold our inventory</p>
            <p>
              Some vendors keep Firefly material on their own premises. A contract
              assembler you drop-ship parts to is the usual case: the goods never reach
              your building, but they are still yours and still need to be somewhere.
              Ticking &quot;can hold our inventory&quot; adds that vendor to the receive
              screen as a place stock can go, and adds a matching row to Locations so
              there is still only one list of places. Untick it and the location is
              retired, not deleted, because boxes received there still point at it.
            </p>
            <p className="font-medium text-on-surface">Disabling a vendor</p>
            <p>
              Disable removes a vendor from the picker lists people use when creating new
              orders, so nobody accidentally orders from a vendor Firefly no longer works
              with. It does not delete anything, past orders and receiving history still
              show that vendor&apos;s name. There is no delete button here on purpose: a
              vendor you could delete is a purchase history with a hole in it.
            </p>
            <p className="font-medium text-on-surface">New catalog items</p>
            <p>
              One more thing, if you mint a brand new part number from the Parts Catalog
              page, it starts at zero quantity. It will not show up as real stock until the
              first box of it is actually received, minting the number and having
              inventory on hand are two separate steps.
            </p>
          </>
        }
        primary={<PrimaryAction onClick={() => setCreating(true)}>+ New Vendor</PrimaryAction>}
        actions={
          /* The taxonomy this list groups by, edited where you notice it needs
             editing. Same rule the catalog follows with part families: a tool
             you reach for BECAUSE you are looking at this screen belongs on it,
             not beside it in the menu. Text tier, because it is a place to go
             and look rather than another way to start work here.
             Same gate as this page, so anybody who can reach the vendor list
             can fix the buckets it groups by. */
          (user.isadmin || user.can_admin || user.can_procurement) ? (
            <SecondaryAction variant="text" to="/admin/vendor-types">
              Vendor types
            </SecondaryAction>
          ) : undefined
        }
      />

      <FilterBar>
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search vendors by name or code"
          placeholder="Search name or code…"
        />
      </FilterBar>

      {/* The vessel is the shared table's: shell, radius, scroll container and
          the face on every row. The search sits in the bar above — it decides
          what `filtered` holds, and the table renders what it is given.

          `emptyMode="replace"` matches what this page has always done: while
          loading, and when nothing matches, the column headers are not there
          either. There is nothing to label yet. */}
      <DataTable<VendorListRow>
        data={filtered}
        columns={VENDOR_COLUMNS}
        getRowId={(r) => r.id}
        meta={{ loadingEditId, onEdit: handleEdit, labelForType }}
        groupBy={search.trim() ? undefined : vendorGroupBy}
        // The row is the preview's own control: role=button plus a real tab
        // stop, so every vendor is reachable by keyboard in reading order.
        // Deliberately NOT `rowKeyboard` — that dialect is one moving
        // selection with J/K and a single Enter target, which is the AP queue's
        // model, not this page's.
        rowProps={(r) => ({
          role: 'button',
          tabIndex: 0,
          'aria-label': `Preview vendor ${r.name}`,
          onClick: () => setPreviewVendorId(r.id),
          onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
            // Only the row's own key events. A keypress on the Edit button
            // bubbles here too, and would open the preview on top of the drawer.
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPreviewVendorId(r.id);
            }
          },
          className: `cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-inset${
            r.is_disabled ? ' opacity-50' : ''
          }`,
        })}
        emptyMode="replace"
        loading={loading}
        loadingContent={
          <SkeletonGroup label="Loading vendors" className="space-y-2 py-6">
            {['w-full', 'w-full', 'w-full', 'w-5/6'].map((w, i) => (
              <Skeleton key={i} className={`h-9 ${w}`} />
            ))}
          </SkeletonGroup>
        }
        empty={
          <EmptyState variant="area">
            {rows.length === 0 ? 'No vendors yet.' : 'No vendors match your search.'}
          </EmptyState>
        }
      />

      {creating && (
        <VendorForm
          mode="create"
          types={types}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            fetchRows();
          }}
        />
      )}
      {editing && (
        <VendorForm
          mode="edit"
          row={editing}
          types={types}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchRows();
          }}
        />
      )}

      {previewVendorId && (
        <VendorPreviewModal
          subject={{ mode: 'existing', vendorId: previewVendorId }}
          onClose={() => setPreviewVendorId(null)}
          // The preview already holds the exact VendorFullRow the edit form
          // needs — handing it straight to setEditing skips a second
          // GET /vendors/:id that handleEdit would otherwise re-fetch.
          onEdit={(row) => setEditing(row)}
        />
      )}
    </PageLayout>
  );
};

/**
 * The vendor form, as a component both this page and an in-place create use.
 *
 * EXPORTED because of the standing rule in web/CLAUDE.md: everything the current
 * task needs is reachable from where the person stands. The New Item wizard's
 * PCBA vendor segment offers "New vendor…", and the assembler it needs is
 * entered without leaving the half-composed item. Building a second, smaller
 * vendor form for that would be two forms that drift.
 */
export interface VendorFormProps {
  mode: 'create' | 'edit';
  row?: VendorFullRow;
  types: VendorTypeOption[];
  /** Preselects the type on a create. The wizard knows it wants a PCBA vendor. */
  defaultType?: string;
  onClose: () => void;
  /**
   * The created or updated vendor, when the server returned one.
   *
   * This page ignores it and refetches, because a save can also have moved a
   * row's type, hold state or sort position and the list is the answer to more
   * than one row. An in-place caller wants exactly the row it just made, so it
   * can drop it into the field that asked for it without a round trip.
   */
  onSaved: (saved?: VendorFullRow) => void;
  /**
   * Render the fields alone, with no DrawerShell around them.
   *
   * A drawer inside a drawer is two focus traps and two backdrops fighting over
   * one Escape key, so a caller that is already inside a modal takes the fields
   * and provides its own frame. It also swaps the `<form>` for a `<div>`: a form
   * nested in a form is invalid HTML, and the browser's recovery is to submit
   * the outer one, which here would mint the item instead of adding the vendor.
   */
  embedded?: boolean;
}

export const VendorForm: React.FC<VendorFormProps> = ({
  mode,
  row,
  types,
  defaultType,
  onClose,
  onSaved,
  embedded = false,
}) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [name, setName] = useState(row?.name || '');
  const [type, setType] = useState(row?.type || defaultType || '');
  const [code, setCode] = useState(row?.code || '');
  const [contactName, setContactName] = useState(row?.contact_name || '');
  const [contactEmail, setContactEmail] = useState(row?.contact_email || '');
  const [contactPhone, setContactPhone] = useState(row?.contact_phone || '');
  const [addressLine1, setAddressLine1] = useState(row?.address_line1 || '');
  const [addressLine2, setAddressLine2] = useState(row?.address_line2 || '');
  const [city, setCity] = useState(row?.city || '');
  const [region, setRegion] = useState(row?.region || '');
  const [postalCode, setPostalCode] = useState(row?.postal_code || '');
  const [country, setCountry] = useState(row?.country || '');
  const [paymentTerms, setPaymentTerms] = useState(row?.payment_terms || '');
  const [notes, setNotes] = useState(row?.notes || '');
  const [qboVendorRef, setQboVendorRef] = useState(row?.qbo_vendor_ref || '');

  const [onHold, setOnHold] = useState(row?.on_hold ?? false);
  const [holdReason, setHoldReason] = useState(row?.hold_reason || '');
  const [releaseDate, setReleaseDate] = useState(row?.release_date || '');
  const [isDisabled, setIsDisabled] = useState(row?.is_disabled ?? false);
  const [canReceive, setCanReceive] = useState(row?.can_receive ?? false);
  const [paymentTermsDays, setPaymentTermsDays] = useState(
    row?.payment_terms_days === null || row?.payment_terms_days === undefined
      ? '' : String(row.payment_terms_days)
  );
  const [paymentTermsBasis, setPaymentTermsBasis] = useState(row?.payment_terms_basis || '');

  const [submitting, setSubmitting] = useState(false);
  // Embedded only: has anybody typed in here yet. Decides whether Escape is
  // an exit or a no-op. See the wrapper at the bottom of this component.
  const [touched, setTouched] = useState(false);

  // Typed work = any field differing from what the form opened with, compared as the save sends it.
  const dirty =
    JSON.stringify([
      name.trim(), type, code.trim().toUpperCase(), contactName.trim(), contactEmail.trim(), contactPhone.trim(),
      addressLine1.trim(), addressLine2.trim(), city.trim(), region.trim(), postalCode.trim(), country.trim(),
      paymentTerms.trim(), notes.trim(), qboVendorRef.trim(), onHold, onHold ? holdReason.trim() : '',
      onHold ? releaseDate : '', isDisabled, canReceive, paymentTermsDays, paymentTermsBasis,
    ]) !==
    JSON.stringify([
      (row?.name || '').trim(), row?.type || defaultType || '', (row?.code || '').trim().toUpperCase(),
      (row?.contact_name || '').trim(), (row?.contact_email || '').trim(), (row?.contact_phone || '').trim(),
      (row?.address_line1 || '').trim(), (row?.address_line2 || '').trim(), (row?.city || '').trim(),
      (row?.region || '').trim(), (row?.postal_code || '').trim(), (row?.country || '').trim(),
      (row?.payment_terms || '').trim(), (row?.notes || '').trim(), (row?.qbo_vendor_ref || '').trim(),
      row?.on_hold ?? false, row?.on_hold ? (row.hold_reason || '').trim() : '',
      row?.on_hold ? row.release_date || '' : '', row?.is_disabled ?? false, row?.can_receive ?? false,
      row?.payment_terms_days === null || row?.payment_terms_days === undefined ? '' : String(row.payment_terms_days),
      row?.payment_terms_basis || '',
    ]);

  const handleDisableToggle = async (checked: boolean) => {
    // Only the transition to disabled is destructive enough to confirm —
    // re-enabling is reversible and needs no gate.
    if (checked && !isDisabled) {
      const ok = await confirm({
        title: 'Disable this vendor',
        tone: 'danger',
        confirmLabel: 'Disable vendor',
        message: `${row?.name || name}\n\nThis hides the vendor from order-form pickers. Past orders and receiving history keep showing its name, and you can re-enable it any time.`,
      });
      if (!ok) return;
    }
    setIsDisabled(checked);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;

    if (!name.trim()) {
      toast.error('A vendor needs a name');
      return;
    }
    if (!type) {
      toast.error('A vendor needs a type');
      return;
    }
    if (code.trim() && !/^[A-Za-z]{2}$/.test(code.trim())) {
      toast.error('Vendor code must be exactly two letters, or left blank');
      return;
    }
    if (onHold && !holdReason.trim()) {
      toast.error('Putting a vendor on hold needs a reason');
      return;
    }

    const body: Record<string, unknown> = {
      name: name.trim(),
      type,
      code: code.trim() ? code.trim().toUpperCase() : null,
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      contact_phone: contactPhone.trim() || null,
      address_line1: addressLine1.trim() || null,
      address_line2: addressLine2.trim() || null,
      city: city.trim() || null,
      region: region.trim() || null,
      postal_code: postalCode.trim() || null,
      country: country.trim() || null,
      payment_terms: paymentTerms.trim() || null,
      notes: notes.trim() || null,
      qbo_vendor_ref: qboVendorRef.trim() || null,
      on_hold: onHold,
      hold_reason: onHold ? holdReason.trim() : null,
      release_date: onHold && releaseDate ? releaseDate : null,
      is_disabled: isDisabled,
      can_receive: canReceive,
      // Empty string means "not recorded" and must go as null, not 0 —
      // 0 is a real answer meaning due immediately.
      payment_terms_days: paymentTermsDays === '' ? null : parseInt(paymentTermsDays, 10),
      payment_terms_basis: paymentTermsBasis || null,
    };
    // Half a clock computes nothing and would snapshot onto every future PO.
    if ((paymentTermsDays === '') !== (paymentTermsBasis === '')) {
      toast.error(paymentTermsBasis === ''
        ? 'Say what the net days count from, or clear them.'
        : 'Net days are needed for that payment clock, or clear what it counts from.');
      return;
    }

    setSubmitting(true);
    try {
      // POST answers 201 with the created row and PATCH with the updated one,
      // so the caller is handed what it just made rather than being told it
      // happened and left to go and find it.
      let saved: VendorFullRow | undefined;
      if (mode === 'create') {
        saved = (await api.post('/procurement/vendors', { body })) as VendorFullRow;
        toast.success(`Created vendor ${name.trim()}`);
      } else if (row) {
        saved = (await api.patch(`/procurement/vendors/${row.id}`, { body })) as VendorFullRow;
        toast.success(`Updated ${row.name}`);
      }
      onSaved(saved);
    } catch (err: any) {
      // The API's message is already the specific, useful sentence (duplicate
      // name/code, hold_reason_required, etc.) — just surface it.
      toast.error(err?.data?.error?.message || err?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = (
    <>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelClass}>Name</span>
            <Field
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Digi-Key"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Type</span>
            <Select
              label="Type"
              placeholder="Select a type…"
              value={type}
              onChange={setType}
              options={types.map((t) => ({ key: t.key, label: t.label }))}
            />
          </label>
        </div>

        <label className="block">
          <span className={labelClass}>Code</span>
          <Field
            className="max-w-[8rem] font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. DK"
            maxLength={2}
          />
          <p className="mt-1 text-xs text-on-surface-variant">
            Exactly two letters, or leave blank. Only used for vendors that appear in a
            board SKU.
          </p>
        </label>

        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className={labelClass}>Contact name</span>
            <Field value={contactName} onChange={(e) => setContactName(e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Contact email</span>
            <Field
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
          </label>
          <label className="block">
            <span className={labelClass}>Contact phone</span>
            <Field value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelClass}>Address line 1</span>
            <Field value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Address line 2</span>
            <Field value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
          </label>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <label className="block">
            <span className={labelClass}>City</span>
            <Field value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Region</span>
            <Field value={region} onChange={(e) => setRegion(e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Postal code</span>
            <Field value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </label>
          <label className="block">
            <span className={labelClass}>Country</span>
            <Field value={country} onChange={(e) => setCountry(e.target.value)} />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={labelClass}>Payment terms</span>
            <Field
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="e.g. Net 30"
            />
            <p className="mt-1 text-xs text-on-surface-variant">
              However the vendor words it. The two fields below are the same thing as
              numbers, for working out when cash actually leaves.
            </p>
          </label>
          {/* Migration 034/035. The computable half, beside the prose above.
              Left blank for anything the numbers cannot express — a split
              schedule like "50% deposit, balance on delivery" stays in the text
              field and these stay empty, because half a truth here would
              compute a confidently wrong cash date. */}
          <div className="grid grid-cols-2 gap-3">
            <PaymentClockFields
              idPrefix="vendor-clock"
              days={paymentTermsDays}
              basis={paymentTermsBasis}
              onDaysChange={setPaymentTermsDays}
              onBasisChange={setPaymentTermsBasis}
              labelClassName={labelClass}
            />
          </div>
          <label className="block">
            <span className={labelClass}>QBO vendor ref</span>
            <Field value={qboVendorRef} onChange={(e) => setQboVendorRef(e.target.value)} />
          </label>
        </div>

        <label className="block">
          <span className={labelClass}>Notes</span>
          <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="space-y-3 border-t border-outline-variant pt-4">
          <Checkbox
            checked={onHold}
            onChange={(e) => setOnHold(e.target.checked)}
            label="On hold"
          />
          <Reveal show={onHold}>
            <div className="grid grid-cols-2 gap-3 pl-6">
              <label className="block">
                <span className={labelClass}>Reason (required)</span>
                <Field
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  placeholder="Why is this vendor on hold?"
                />
              </label>
              <label className="block">
                <span className={labelClass}>Hold lifts on</span>
                <Field
                  type="date"
                  value={releaseDate}
                  onChange={(e) => setReleaseDate(e.target.value)}
                />
              </label>
            </div>
          </Reveal>

          <Checkbox
            checked={isDisabled}
            onChange={(e) => handleDisableToggle(e.target.checked)}
            label="Disabled"
          />

          {/* Migration 029. Flipping this on creates a matching row in
              Locations, so the receive screen can file stock to this vendor's
              premises. Off retires that row rather than deleting it: boxes
              already received there still point at it. */}
          <Checkbox
            checked={canReceive}
            onChange={(e) => setCanReceive(e.target.checked)}
            label="Can hold our inventory"
          />
          <p className="-mt-1 ml-6 max-w-prose text-xs text-on-surface-variant">
            For a contract assembler or finisher you drop-ship parts to. Adds them to
            the receive screen as a place stock can go, so material that never reaches
            your building is still on the books. Switching it off later takes them out
            of the picker and leaves the history intact.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className={buttonClass('quiet')}
          >
            Cancel
          </button>
          <button
            // In embedded mode there is no form to submit, and the outer form's
            // Create belongs to something else entirely.
            type={embedded ? 'button' : 'submit'}
            onClick={embedded ? () => handleSubmit() : undefined}
            disabled={submitting}
            className={buttonClass('filled')}
          >
            {submitting
              ? 'Saving…'
              : mode === 'create'
                // Two buttons reading "Create" on one screen is one button too
                // many: this one names the thing it makes.
                ? (embedded ? 'Add vendor' : 'Create')
                : 'Save'}
          </button>
        </div>
    </>
  );

  if (embedded) {
    return (
      <div
        className="space-y-4"
        // One listener rather than a flag per field: React's change event
        // bubbles, so anything typed anywhere in here counts as work worth
        // protecting. The preselected type does not, because nobody typed it.
        onChange={() => { if (!touched) setTouched(true); }}
        onKeyDown={(e) => {
          const tag = (e.target as HTMLElement).tagName;
          // Enter inside these FIELDS would otherwise reach the OUTER form and
          // submit it, so here it means "add the vendor". On a BUTTON it means
          // what the button says: Enter on Cancel must cancel, not create a
          // vendor that then cannot be deleted. Textareas keep their newline.
          if (e.key === 'Enter' && tag !== 'BUTTON' && tag !== 'TEXTAREA') {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit();
          }
          // The host decides what Escape means, and it must not reach a drawer
          // above and close the whole thing over a sub-form. Once something is
          // typed the key does nothing at all: a vendor half entered is work,
          // and Cancel is the exit that says so out loud.
          if (e.key === 'Escape' && tag !== 'SELECT') {
            e.stopPropagation();
            if (!touched) onClose();
          }
        }}
      >
        {fields}
      </div>
    );
  }

  return (
    <DrawerShell title={mode === 'create' ? 'New Vendor' : `Edit: ${row?.name}`} onClose={onClose} size="lg" dirty={dirty}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {fields}
      </form>
    </DrawerShell>
  );
};

export default VendorsAdmin;
